/**
 * Звуки колебания в речи: детерминированный счёт и фильтр для показа.
 *
 * До #819 «Слова-паразиты» считала модель анализа, и счёт был недостоверен:
 * на записи 1952ef43 — `{"count": 0}` при двадцати «е-е-е»; за 30 дней в проде —
 * `4.02026`, `222026`, `360300000000000000`; ноль у 98.6% звонков, где агент не
 * опознан.
 *
 * Считаются и скрываются ТОЛЬКО звуки колебания («е-е-е», «ммм», «эээ», «um»).
 * Слов здесь нет намеренно, и это вывод A/B-оценки, а не осторожность на глаз:
 * из 392 кандидатов в 38 прод-звонках 251 — «ну», и модель, которой прямо
 * велели убрать паразиты, оставила 71% из них как значимые («ну давайте», «ну
 * добре»); «короче» — 6 из 13. У регулярного выражения контекста нет вовсе, а
 * счёт ложится в оценку оператора: недосчитать честнее, чем приписать.
 *
 * Двухбуквенные формы без дефиса тоже исключены: «мм» — миллиметры («труба
 * 20 мм»), «ее» — «её», набранное без ё. Протяжные формы любой длины («Ееее»,
 * «е-е-е-е», «Мммм») считаются — ASR пишет длину звука как слышит.
 */

/**
 * Один звук колебания. Каждая альтернатива — либо через дефис (любая длина от
 * двух), либо слитно от трёх букв.
 */
const HESITATION = [
  // кириллица
  'е(?:-е)+',
  'е{3,}',
  'э(?:-э)+',
  'э{3,}',
  'а(?:-а)+',
  'а{3,}',
  'м(?:-м)+',
  'м{3,}',
  '[еэ]-м+',
  '[еэ]м{2,}',
  // Только через «э»: «ем» — это глагол, а «эм» вне звука колебания
  // встречается лишь как название буквы.
  'эм',
  'хм+',
  // латиница
  'm{3,}',
  'u+m+',
  'u+h+',
  'h+m+',
  'e+rm+',
  'e+h+',
  'e+m{2,}',
].join('|');

/**
 * Границы: ни буквы, ни цифры, ни дефиса по краям. `\b` в JS опирается на
 * латинский `\w` и на «е-е-е» не срабатывает; дефис исключён, чтобы звук не
 * находился внутри составного слова.
 */
const HESITATION_REGEX = new RegExp(
  `(?<![\\p{L}\\p{N}-])(?:${HESITATION})(?![\\p{L}\\p{N}-])`,
  'giu'
);

/**
 * Та же регулярка без `g`: `test` у глобальной хранит `lastIndex` и на
 * повторном вызове отвечает про другое место текста.
 */
const HESITATION_TEST = new RegExp(HESITATION_REGEX.source, 'iu');

/**
 * Есть ли в тексте звук колебания — вопрос о самом тексте, без правил показа.
 *
 * `hesitationEdits` на строке из одних звуков отдаёт пустой список (реплику из
 * «Ммм.» мы не опустошаем), и по нему нельзя судить, есть ли там звук.
 */
export function hasHesitation(text: string): boolean {
  return HESITATION_TEST.test(text);
}

/** Сколько примеров показываем в отчёте — столько же просил промпт. */
const MAX_EXAMPLES = 3;

export interface FillerWordsCountResult {
  count: number;
  /** Самые частые формы, как их записал ASR (в нижнем регистре). */
  examples: string[];
}

/** Посчитать звуки колебания в произвольном тексте. */
export function countFillerWords(text: string): FillerWordsCountResult {
  if (!text) return { count: 0, examples: [] };

  // `matchAll` не зависит от lastIndex общей глобальной регулярки.
  const hits = [...text.matchAll(HESITATION_REGEX)].map((match) =>
    match[0].toLowerCase()
  );

  const frequency = new Map<string, number>();
  for (const hit of hits) {
    frequency.set(hit, (frequency.get(hit) ?? 0) + 1);
  }

  const examples = [...frequency.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .slice(0, MAX_EXAMPLES)
    .map(([word]) => word);

  return { count: hits.length, examples };
}

export interface FillerCountableSegment {
  speaker: string;
  text: string;
}

/**
 * Посчитать звуки колебания ТОЛЬКО у агента.
 *
 * `null` — «посчитать нельзя», и это не то же самое, что ноль: без сегментов
 * или без опознанного агента честного ответа нет, а ноль в отчёте клиент
 * читает как факт «паразитов не было».
 */
export function countAgentFillerWords(
  segments: readonly FillerCountableSegment[] | undefined,
  agentSpeakerLabels: readonly string[] | undefined
): FillerWordsCountResult | null {
  if (!segments?.length || !agentSpeakerLabels?.length) return null;

  const agents = new Set(agentSpeakerLabels.map((label) => label.toLowerCase()));
  const agentText = segments
    .filter((segment) => agents.has(segment.speaker.toLowerCase()))
    .map((segment) => segment.text)
    .join(' ');

  if (!agentText.trim()) return null;

  return countFillerWords(agentText);
}

/** Правка показа: заменить `[start, end)` исходного текста на `shown`. */
export interface DisplayEdit {
  start: number;
  end: number;
  shown: string;
  /**
   * Правка неделима: позиции внутри неё в исходном тексте не соответствует ни
   * одна позиция показа (маркер вложения и его подпись). Неатомарная правка той
   * же длины переводится посимвольно (перенос заглавной).
   */
  atomic?: boolean;
}

/**
 * Правки, скрывающие звуки колебания, — в координатах исходного текста.
 *
 * Звук уходит вместе с запятой и пробелами после него («Е-е-е, перша» →
 * «Перша»), а если стоял последним перед точкой — вместе с запятой перед ним
 * («тобто, е-е-е.» → «тобто.»). Заглавная буква звука в начале фразы переходит
 * на следующее слово: так фраза не начинается со строчной. Правка буквы —
 * отдельная правка той же длины, поэтому карта позиций её не замечает.
 *
 * Звук, который сам по себе целая фраза, уносит и свой знак конца с
 * разделителем: иначе он остаётся сиротой («Ммм. Добре.» → «. Добре.»,
 * «Аааа! Що це?» → «! Що це?», «Е-е-е: добре.» → «: добре.»).
 *
 * Реплика, состоящая из одних звуков («Мм.», «Е-е-е...»), показывается как
 * есть: пустая строка в диалоге хуже честного «е-е-е».
 */
export function hesitationEdits(text: string): DisplayEdit[] {
  if (!text) return [];

  // Проход 1: удаляемые диапазоны. Подряд идущие звуки («Е-е-е, е-е-е, перша»)
  // сливаются в один — иначе заглавная первого перенеслась бы на второй звук.
  const removals: Array<{
    start: number;
    end: number;
    capital: boolean;
    startsSentence: boolean;
  }> = [];
  for (const match of text.matchAll(HESITATION_REGEX)) {
    let start = match.index ?? 0;
    let end = start + match[0].length;

    const before = text.slice(0, start);
    const trailing = /^[,…]?\s*/u.exec(text.slice(end))?.[0] ?? '';
    const rest = text.slice(end + trailing.length);
    // Любой знак после звука принадлежит фразе, а не ему: и точка, и
    // соединительные — двоеточие, точка с запятой, тире. Лишним оказывается
    // разделитель ПЕРЕД звуком, иначе остаётся «Ну, : добре».
    const atEnd = rest === '' || /^[.!?…:;—–-]/u.test(rest);
    // Перед звуком либо ничего, либо конец другой фразы — значит звук и есть
    // фраза целиком, и знак конца после него принадлежит ему. Открывающая
    // кавычка, скобка и тире реплики — тоже начало фразы, иначе «— Ммм.
    // Добре.» показывалось бы как «—. Добре.».
    const wholeSentence =
      before.trim() === '' ||
      /[.!?…]["»)\]]?\s*$/u.test(before) ||
      /[«»„“‟‹"'‘‚([]\s*$/u.test(before) ||
      /^\s*[—–-]\s*$/u.test(before);

    if (wholeSentence) {
      end += /^[,…]?\s*[.!?…:;—–-]*\s*/u.exec(text.slice(end))?.[0].length ?? 0;
      // Дальше текста нет: пробел перед звуком тоже лишний.
      if (end >= text.length) start -= (/\s*$/u.exec(before)?.[0] ?? '').length;
    } else if (atEnd) {
      // Звук перед знаком: убрать запятую и пробелы ПЕРЕД ним, знак оставить.
      // Если остающийся знак — соединительный, такой же перед звуком уносится
      // вместе с ним: ASR ставит их парой вокруг паузы («Привет — ммм —
      // пока»), и оставшаяся половина висит без второй.
      const leading = (
        /^[:;—–-]/u.test(rest) ? /[,\s]*[:;—–-]?[,\s]*$/u : /[,\s]*$/u
      ).exec(before)?.[0];
      start -= (leading ?? '').length;
      end += trailing.replace(/\s+$/u, '').length;
    } else {
      end += trailing.length;
    }

    const first = match[0][0] ?? '';
    const capital = first !== first.toLowerCase();
    const previous = removals[removals.length - 1];
    if (previous && start <= previous.end) {
      previous.end = Math.max(previous.end, end);
    } else {
      removals.push({
        start: Math.max(0, start),
        end,
        capital,
        startsSentence: wholeSentence,
      });
    }
  }

  // Проход 2: правки. Заглавная звука в начале фразы переходит на первое
  // слово после удалённого диапазона.
  const edits: DisplayEdit[] = [];
  for (const removal of removals) {
    edits.push({ start: removal.start, end: removal.end, shown: '' });
    // Только если звук НАЧИНАЛ фразу: иначе его заглавная — привычка ASR
    // писать паузу с большой буквы посреди предложения, и перенос давал бы
    // «Я, Ммм, думаю» → «Я, Думаю».
    const next = text[removal.end];
    if (
      removal.capital &&
      removal.startsSentence &&
      next !== undefined &&
      next !== next.toUpperCase()
    ) {
      edits.push({ start: removal.end, end: removal.end + 1, shown: next.toUpperCase() });
    }
  }

  if (edits.length === 0) return edits;
  return /[\p{L}\p{N}]/u.test(applyDisplayEdits(text, edits)) ? edits : [];
}

/** Применить правки показа (отсортированы по началу, не пересекаются). */
export function applyDisplayEdits(text: string, edits: readonly DisplayEdit[]): string {
  let out = '';
  let cursor = 0;
  for (const edit of edits) {
    out += text.slice(cursor, edit.start) + edit.shown;
    cursor = edit.end;
  }
  return out + text.slice(cursor);
}

/** Текст реплики для показа человеку: без звуков колебания. */
export function stripHesitations(text: string): string {
  return applyDisplayEdits(text, hesitationEdits(text));
}
